import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "../assets/crmPermissionView.module.css";

const CrmPermissionView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [permission, setPermission] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    loadPermissionData();
  }, [id]);

  const loadPermissionData = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/permissions/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPermission(data.data.permission || null);
          setUsers(data.data.users || []);
          setApiFailed(false);
        } else {
          throw new Error(data.message || 'Failed to load permission data');
        }
      } else {
        throw new Error('Failed to load permission data');
      }
    } catch (error) {
      console.error("Failed to load permission data:", error);
      setApiFailed(true);
      
      // Fallback to mock data
      const mockPermissions = {
        1: {
          id: 1,
          display_name: "Manage Users",
          name: "users.manage",
          route: "users.manage",
          description: "Allows users to create, edit, and delete user accounts"
        },
        2: {
          id: 2,
          display_name: "View Users",
          name: "users.view",
          route: "users.view",
          description: "Allows users to view user accounts"
        },
        3: {
          id: 3,
          display_name: "Manage Roles",
          name: "roles.manage",
          route: "roles.manage",
          description: "Allows users to create, edit, and delete roles"
        }
      };
      
      const mockUsers = [
        { id: 1, name: "John Smith", email: "john.smith@example.com", role: "System Administrator" },
        { id: 2, name: "Jane Doe", email: "jane.doe@example.com", role: "Senior Management" }
      ];
      
      setPermission(mockPermissions[id] || { id: parseInt(id), display_name: "Unknown Permission", name: "unknown", route: "unknown", description: "No description available" });
      setUsers(mockUsers);
      // Error handled silently - no toast shown
    } finally {
      setLoading(false);
    }
  };

  if (loading && !permission) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default panel-standard">
              <div className="panel-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!permission || !permission.display_name) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default panel-standard">
              <div className="panel-body text-center">
                <p>Permission not found</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/permissions">CRM Permissions</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {permission.display_name || 'Permission Details'}
          </li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div className="panel-heading panel-title">
              <h3>Permission Details: {permission.display_name || 'Unknown Permission'}</h3>
            </div>
            <div className="panel-body">
              {/* Permission Information */}
              <div className="row">
                <div className="col-md-6">
                  <dl className="dl-horizontal">
                    <dt>Display Name:</dt>
                    <dd>{permission.display_name || 'N/A'}</dd>
                    
                    <dt>Name:</dt>
                    <dd>{permission.name || 'N/A'}</dd>
                    
                    <dt>Route:</dt>
                    <dd>{permission.route || 'N/A'}</dd>
                    
                    <dt>Description:</dt>
                    <dd>{permission.description || 'N/A'}</dd>
                  </dl>
                </div>
              </div>

              {/* Users with this Permission */}
              <div style={{ marginTop: '30px' }}>
                <h4>Users with this Permission</h4>
                {users && users.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(users) ? users.map((user) => (
                          <tr key={user.id}>
                            <td>{user.id || 'N/A'}</td>
                            <td>{user.name || 'N/A'}</td>
                            <td>{user.email || 'N/A'}</td>
                            <td>{user.role || 'N/A'}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="4">No users available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center">
                    <p>No users found with this permission</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmPermissionView; 